<script lang="ts">
    import { fade } from "svelte/transition"
    import HighlightChooser from "./HighlightChooser.svelte"
    import { createEventDispatcher, onDestroy } from "svelte"
    import Icon from "@components/material/Icon.svelte"
    import { canShare } from "@utils/reading/copy"
    import { findHighlight, type Highlight, type HighlightID } from "@store/highlights"
    import { longPress } from "@utils/directives/longPress"
    import { blockListenerPropagation } from "@utils/highlights/directive-logic/selection-logic"
    import { GrabberState } from "@utils/highlights/directive-logic/global-select-handler"
    import {
        HighlightSettingLimiter,
        type HighlighterListenerSettings,
    } from "@utils/highlights/directive-logic/highlight-listener-permissions"
    import { IMAGE_QUOTE_ALLOWED } from "@utils/image-editor/image-quote"
    export let pusherHeight: number = 0
    export let grabberState: GrabberState
    export let highlightID: HighlightID | null
    export let permissions: HighlighterListenerSettings
    $: highlight = highlightID !== null ? findHighlight(highlightID) : null

    export let canQuoteImage
    export let visible = true
    const dispatch = createEventDispatcher()
</script>

<div
    class="selectionPopupContainer {blockListenerPropagation} {grabberState === GrabberState.MOVING
        ? 'opacity-0 pointer-events-none'
        : 'pointer-events-auto'}"
>
    <div class="selectionPusher" style:height="{pusherHeight - 60}px" />
    <!-- <div class="antiPusher" /> -->
    {#if visible}
        <div
            transition:fade|global={{ duration: 150 }}
            class="selectionPopup"
            style:top="calc(1.25rem + var(--context-menu-top-offset, 0px))"
        >
            <div
                class="selectionPopupClickable"
                on:pointerdown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                }}
            >
                {#if $canShare}
                    <div>
                        <span
                            tabindex="0"
                            role="button"
                            on:keydown={() => dispatch("share")}
                            on:pointerdown|stopPropagation
                            on:pointerup|stopPropagation|preventDefault={() => dispatch("share")}
                            aria-label="share"><Icon icon="share" /></span
                        >
                    </div>
                {/if}

                <div>
                    <span
                        aria-disabled={!canQuoteImage || !IMAGE_QUOTE_ALLOWED}
                        tabindex="0"
                        role="button"
                        on:keydown={() => dispatch("image")}
                        on:pointerdown|stopPropagation
                        on:pointerup|stopPropagation|preventDefault={() => dispatch("image")}
                        aria-label="image"
                        ><Icon
                            icon="image"
                            class={!canQuoteImage || !IMAGE_QUOTE_ALLOWED ? "opacity-50" : ""}
                        /></span
                    >
                </div>
                {#if permissions.editHighlights === HighlightSettingLimiter.ALL || (permissions.editHighlights === HighlightSettingLimiter.EMPHASIZED && highlightID === permissions.emphasizeHighlight)}
                    <div class="popupNote">
                        <span
                            tabindex="0"
                            role="button"
                            on:keydown={() => dispatch("note")}
                            aria-label="note"
                            on:pointerdown|stopPropagation
                            on:pointerup|stopPropagation|preventDefault={() => dispatch("note")}
                            ><Icon icon="pen" /></span
                        >
                    </div>
                {/if}
                {#if permissions.createHighlights}
                    <div class="popupHighlight">
                        <HighlightChooser {highlight} on:highlight on:deletehighlight />
                    </div>
                {/if}
                <div class="popupCopy" class:pop={false}>
                    <span
                        tabindex="0"
                        role="button"
                        aria-label="copy"
                        use:longPress={{
                            stopPropagation: true,
                            callback: () => dispatch("copyalt"),
                            callbackShortpress: () => dispatch("copy"),
                        }}><Icon icon="copy" /></span
                    >
                </div>
            </div>
        </div>
    {/if}
</div>

<style>
.popupCopy {
  @apply relative;
}
.popupCopy:after {
  @apply rounded-full absolute w-0 h-0 transition-none;
  content: "";
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  opacity: 1;
  background-color: var(--darkpurple-);
}
.popupCopy.pop:after {
  transition: width 0.3s linear, height 0.3s linear, opacity 0.3s linear;
  width: 7.5rem;
  opacity: 0;
  height: 7.5rem;
}

.selectionPopupContainer {
  z-index: 4;
  @apply inset-0 absolute pointer-events-none transition-opacity;
}

.antiPusher {
  @apply h-0 mb-14;
}

.selectionPopup {
  @apply w-full bottom-5 flex flex-row justify-center sticky;
}
.selectionPopup .selectionPopupClickable {
  @apply gap-1 px-2 rounded-xl flex items-center flex-row;
  pointer-events: all;
  background-color: var(--popup-background);
  box-shadow: var(--popup-shadow);
}
.selectionPopup .selectionPopupClickable > div {
  @apply p-2;
}
.selectionPopup .selectionPopupClickable > div > span > :global(svg) {
  @apply w-6 text-white;
}
</style>
